import React, { useMemo, useRef, useState, useEffect } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { GripVertical, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { Button } from '@/components/ui/button'
import { useFlowNotes } from '@/contexts/FlowNotesProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { Input } from '@/components/ui/input'

interface EditableItemProps {
  content: string
  onSave: (newContent: { title: string; content: string }) => void
  onDelete: () => void
  listeners: any
  title?: string
  disableDelete: boolean
  autoFocus?: boolean
}

export const EditableItem: React.FC<EditableItemProps> = ({
  content,
  onSave,
  onDelete,
  listeners,
  title,
  disableDelete,
  autoFocus,
}) => {
  const { isModerator, isDebator, negativeUsers, affirmativeUsers, currentUser } = useHallUsers()
  const { currentPage } = useFlowNotes()
  const [open, setOpen] = useState(false)
  const [localContent, setLocalContent] = useState(content)
  const [localTitle, setLocalTitle] = useState(title || '')
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (autoFocus && !open) setOpen(true)
  }, [autoFocus])

  useEffect(() => {
    if (open && autoFocus && textareaRef.current) textareaRef.current.focus()
  }, [open, autoFocus])

  const isEditable = useMemo(() => {
    if (isModerator) return true

    if (!isDebator || !currentUser) return false

    if (currentPage.side === 'AFF' && affirmativeUsers.includes(currentUser.id)) {
      return true
    }

    if (currentPage.side === 'NEG' && negativeUsers.includes(currentUser.id)) {
      return true
    }

    return false
  }, [isModerator, isDebator, currentUser, currentPage.side, affirmativeUsers, negativeUsers])

  const handleOpenChange = (nextOpen: boolean) => {
    if (isEditable) {
      setOpen(nextOpen)
      if (nextOpen) {
        setLocalContent(content)
        setLocalTitle(title || '')
      }
    }
  }

  const handleSave = () => {
    onSave({ title: localTitle, content: localContent })
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        handleSave()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, localContent, localTitle])

  return (
    <div ref={wrapperRef} className="relative w-full flex items-center">
      {!open && isEditable && !disableDelete && (
        <Button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onDelete()
          }}
          className="absolute top-0 right-0 p-0 mt-[-5px] mr-[-5px] h-[20px] w-[20px] hover:text-white bg-gray-600 hover:bg-gray-500 dark:hover:border-gray-300 dark:bg-gray-900 rounded-full text-white"
        >
          <X size={10} />
        </Button>
      )}

      {open ? (
        <div className="w-full space-y-2">
          <Input
            className="w-full border border-gray-300 rounded px-2 py-1 font-semibold text-sm"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter title..."
            disabled={disableDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSave()
              }
            }}
          />
          <Textarea
            ref={textareaRef}
            className="w-full h-24 border border-gray-300 rounded p-2"
            value={localContent}
            onChange={(e) => setLocalContent(e.target.value)}
            placeholder="Enter content..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSave()
              }
            }}
          />
        </div>
      ) : (
        <div
          className={`flex items-start py-2 pr-2 pl-0 border border-gray-700 hover:bg-gray-300 dark:hover:bg-gray-800
             ${
               isEditable ? 'cursor-pointer' : 'cursor-default'
             } rounded w-full space-x-4 overflow-hidden`}
          onClick={() => handleOpenChange(true)}
        >
          <span className="h-full cursor-grab" {...listeners}>
            {isEditable && <GripVertical size={20} />}
          </span>

          <span className="w-full">
            <div className="whitespace-pre-wrap break-words max-w-full overflow-hidden">
              {localTitle && <h1 className="font-semibold text-sm mb-1">{localTitle}</h1>}
              {content.trim() ? (
                <ReactMarkdown
                  children={content.replace(/\\n/g, '\n')}
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                />
              ) : (
                <span className="text-gray-400">Click to edit...</span>
              )}
            </div>
          </span>
        </div>
      )}
    </div>
  )
}
